import { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, Instagram, ArrowUpRight } from "lucide-react";
import { NAV_LINKS, SITE } from "@/constants/site";
import { NAV } from "@/constants/testIds";

const Nav = () => {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setOpen(false);
  }, [location.pathname]);

  return (
    <header
      data-testid={NAV.root}
      className={`fixed top-0 left-0 right-0 z-50 transition-[background-color,border-color] duration-300 ${
        scrolled
          ? "bg-[#0A0A0A]/80 backdrop-blur-xl border-b border-white/10"
          : "bg-transparent border-b border-transparent"
      }`}
    >
      <div className="mx-auto max-w-[1440px] px-5 md:px-10 h-[72px] flex items-center justify-between">
        <Link
          to="/"
          data-testid={NAV.brand}
          className="flex items-center gap-3 group"
        >
          <div className="relative w-10 h-10 rounded-full overflow-hidden ring-1 ring-white/10 bg-black">
            <img
              src={SITE.logo}
              alt="TWGAS"
              className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
            />
          </div>
          <div className="hidden sm:flex flex-col leading-none">
            <span className="font-display text-[22px] tracking-wide">
              #TWGAS
            </span>
            <span className="font-mono-plex text-[9px] tracking-[0.28em] text-zinc-500 uppercase mt-1">
              Athletics · Dev LLC
            </span>
          </div>
        </Link>

        <nav className="hidden md:flex items-center gap-1">
          {NAV_LINKS.map((l) => (
            <NavLink
              key={l.to}
              to={l.to}
              end={l.to === "/"}
              data-testid={NAV.link(l.label.toLowerCase())}
              className={({ isActive }) =>
                `relative px-4 py-2 font-mono-plex text-[11px] tracking-[0.22em] uppercase transition-colors ${
                  isActive
                    ? "text-white"
                    : "text-zinc-400 hover:text-white"
                }`
              }
            >
              {({ isActive }) => (
                <>
                  {l.label}
                  {isActive && (
                    <motion.span
                      layoutId="nav-underline"
                      className="absolute left-3 right-3 -bottom-0 h-[2px] bg-[#FF4D00]"
                    />
                  )}
                </>
              )}
            </NavLink>
          ))}
        </nav>

        <div className="hidden md:flex items-center gap-3">
          <a
            href={SITE.instagramUrl}
            target="_blank"
            rel="noreferrer"
            className="w-10 h-10 flex items-center justify-center border border-white/10 text-zinc-300 hover:text-[#FF4D00] hover:border-[#FF4D00] transition-colors"
            aria-label="Instagram"
          >
            <Instagram size={16} strokeWidth={1.5} />
          </a>
          <Link
            to="/contact"
            data-testid={NAV.ctaContact}
            className="group inline-flex items-center gap-2 pl-4 pr-3 py-2.5 bg-[#FF4D00] text-black font-display text-[15px] tracking-[0.08em] uppercase hover:bg-white transition-colors"
          >
            Let's Work
            <ArrowUpRight
              size={16}
              className="transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5"
            />
          </Link>
        </div>

        <button
          data-testid={NAV.mobileToggle}
          onClick={() => setOpen((v) => !v)}
          className="md:hidden w-10 h-10 flex items-center justify-center border border-white/10 text-white"
          aria-label="Toggle menu"
        >
          {open ? <X size={18} /> : <Menu size={18} />}
        </button>
      </div>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
            className="md:hidden overflow-hidden bg-[#0A0A0A] border-t border-white/10"
          >
            <div className="px-5 py-6 flex flex-col gap-1">
              {NAV_LINKS.map((l, i) => (
                <motion.div
                  key={l.to}
                  initial={{ opacity: 0, x: -12 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.05 * i }}
                >
                  <NavLink
                    to={l.to}
                    end={l.to === "/"}
                    className={({ isActive }) =>
                      `flex items-center justify-between py-4 border-b border-white/5 font-display text-3xl uppercase ${
                        isActive ? "text-[#FF4D00]" : "text-white"
                      }`
                    }
                  >
                    <span>{l.label}</span>
                    <ArrowUpRight size={20} />
                  </NavLink>
                </motion.div>
              ))}
              <a
                href={SITE.instagramUrl}
                target="_blank"
                rel="noreferrer"
                className="mt-6 flex items-center justify-between py-3 text-zinc-400 font-mono-plex text-[11px] tracking-[0.22em] uppercase"
              >
                <span>Instagram · {SITE.instagramHandle}</span>
                <Instagram size={16} />
              </a>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
};

export default Nav;
